/**
 * Purpose: OPFS (Origin Private File System) storage service for large file attachments
 *
 * How it works:
 * 1. Uses the File System Access API to store large blobs efficiently
 * 2. Files are organized in a flat structure: /attachments/{attachmentId}
 * 3. Provides streaming read/write to avoid memory pressure for large files
 *
 * Key features:
 * - Direct byte access without serialization overhead
 * - Streaming support for large files (videos up to 100MB)
 * - Automatic directory creation
 * - Graceful error handling with detailed logging in dev mode
 */

import { IS_OPFS_SUPPORTED } from "@/lib/environmentDetector";

const ATTACHMENTS_DIR = "attachments";

// OPFS getFileHandle rejects any name containing '/'. Server-issued uploadIds can
// be scoped (e.g. "chatId/uuid"), so encode slashes before using as a filename.
// Exported: everything comparing raw attachment ids against OPFS filenames
// (eviction accounting, the orphan sweep) must use the SAME encoding — raw-id
// lookups silently miss for scoped ids.
export const safeFilename = (id: string) => id.replace(/\//g, "%2F");

/**
 * Get or create the attachments directory in OPFS
 */
async function getAttachmentsDirectory(): Promise<FileSystemDirectoryHandle | null> {
  if (!IS_OPFS_SUPPORTED) {
    return null;
  }

  try {
    const root = await navigator.storage.getDirectory();
    return await root.getDirectoryHandle(ATTACHMENTS_DIR, { create: true });
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.error("[OPFS] Failed to get attachments directory:", error);
    }
    return null;
  }
}

/**
 * Write a blob to OPFS
 */
export async function writeToOPFS(
  attachmentId: string,
  blob: Blob,
): Promise<boolean> {
  const directory = await getAttachmentsDirectory();
  if (!directory) {
    return false;
  }

  try {
    const fileHandle = await directory.getFileHandle(
      safeFilename(attachmentId),
      {
        create: true,
      },
    );
    const writable = await fileHandle.createWritable();
    try {
      await writable.write(blob);
      await writable.close();
      return true;
    } catch (error) {
      // Release the stream on failure: an unclosed FileSystemWritableFileStream
      // keeps the file LOCKED for the rest of the session — every later
      // removeEntry/createWritable on this id then throws
      // NoModificationAllowedError, permanently bricking the attachment.
      try {
        await writable.abort();
      } catch {
        // already closed/aborted — nothing to release
      }
      throw error;
    }
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.error(
        `[OPFS] Failed to write attachment ${attachmentId}:`,
        error,
      );
    }
    return false;
  }
}

/**
 * Read a blob from OPFS
 */
export async function readFromOPFS(attachmentId: string): Promise<Blob | null> {
  const directory = await getAttachmentsDirectory();
  if (!directory) {
    return null;
  }

  try {
    const fileHandle = await directory.getFileHandle(
      safeFilename(attachmentId),
    );
    const file = await fileHandle.getFile();
    return file;
  } catch (error) {
    if (error instanceof DOMException && error.name === "NotFoundError") {
      return null;
    }
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.error(`[OPFS] Failed to read attachment ${attachmentId}:`, error);
    }
    return null;
  }
}

/**
 * Delete a file from OPFS
 */
export async function deleteFromOPFS(attachmentId: string): Promise<boolean> {
  const directory = await getAttachmentsDirectory();
  if (!directory) {
    return false;
  }

  try {
    await directory.removeEntry(safeFilename(attachmentId));
    return true;
  } catch (error) {
    if (error instanceof DOMException && error.name === "NotFoundError") {
      return true;
    }
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.error(
        `[OPFS] Failed to delete attachment ${attachmentId}:`,
        error,
      );
    }
    return false;
  }
}

/**
 * Check if a file exists in OPFS
 */
export async function existsInOPFS(attachmentId: string): Promise<boolean> {
  const directory = await getAttachmentsDirectory();
  if (!directory) {
    return false;
  }

  try {
    await directory.getFileHandle(safeFilename(attachmentId));
    return true;
  } catch {
    return false;
  }
}

/** OPFS file info for listing */
export interface OPFSFileInfo {
  id: string;
  size: number;
  lastModified: number;
}

/**
 * List all attachments stored in OPFS
 */
export async function listOPFSAttachments(): Promise<OPFSFileInfo[]> {
  const directory = await getAttachmentsDirectory();
  if (!directory) {
    return [];
  }

  try {
    const files: OPFSFileInfo[] = [];
    for await (const [name, handle] of directory.entries()) {
      if (handle.kind === "file") {
        const file = await (handle as FileSystemFileHandle).getFile();
        files.push({
          id: name,
          size: file.size,
          lastModified: file.lastModified,
        });
      }
    }
    return files;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.error("[OPFS] Failed to list attachments:", error);
    }
    return [];
  }
}

/**
 * Get OPFS storage statistics
 */
export async function getOPFSStats(): Promise<{
  fileCount: number;
  totalSize: number;
}> {
  const files = await listOPFSAttachments();
  return {
    fileCount: files.length,
    totalSize: files.reduce((sum, f) => sum + f.size, 0),
  };
}

/**
 * Clear all attachments from OPFS
 */
export async function clearOPFSAttachments(): Promise<boolean> {
  if (!IS_OPFS_SUPPORTED) {
    return false;
  }

  try {
    const root = await navigator.storage.getDirectory();
    await root.removeEntry(ATTACHMENTS_DIR, { recursive: true });
    return true;
  } catch (error) {
    if (error instanceof DOMException && error.name === "NotFoundError") {
      return true;
    }
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.error("[OPFS] Failed to clear attachments:", error);
    }
    return false;
  }
}
